
import React, { useState } from "react";

import IconMaterial from "@mui/material/Icon/Icon";
import { Tooltip, Button, Fab } from "@mui/material";
import { context } from "../../../context/context";
import { t } from "i18next";
import { Permission } from "../../../context/permission";

import "./style.scss";

export type IconType =
    "add" |
    "arrow_downward" |
    "arrow_upward" |
    "archive" |
    "arrow_back" |
    "cancel" |
    "check" |
    "check_box" |
    "close" |
    "cloud" |
    "cloud_upload" |
    "content_paste" |
    "delete_forever" |
    "delete" |
    "done" |
    "error" |
    "exit_to_app" |
    "flag" |
    "publish" |
    "insert_drive_file" |
    "local_shipping" |
    "lock" |
    "lock_open" |
    "search" |
    "help" |
    "home" |
    "input" |
    "keyboard_arrow_down" |
    "keyboard_arrow_up" |
    "local_see" |
    "list" |
    "mode_edit" |
    "notifications" |
    "note_add" |
    "person" |
    "print" |
    "refresh" |
    "reply" |
    "remove" |
    "save" |
    "settings" |
    "settings_ethernet" |
    "swap_vert" |
    "undo" |
    "vertical_align_top" |
    "play_circle_filled" |
    "view_comfy" |
    "warning" |
    "report_problem" |
    "date_range";

export interface IIconProps {
    align?: "left" | "right";
    disabled?: boolean;
    icon: IconType | JSX.Element;
    isHidden?: boolean;
    isRounded?: boolean;
    secondaryIcons?: IconType[];
    onClick?: (event: any) => void;
    type?: "submit" | "reset" | "button";
    className?: string;
    rightText?: string;
    contained?: boolean;
    tooltipTitle?: string;
    autoFocus?: boolean;
    color?: "inherit" | "primary" | "secondary";
    permission?: Permission;
    tooltipPlacement?: "bottom-end"
    | "bottom-start"
    | "bottom"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
}

export const Icon = (props: IIconProps) => {
    const hasPermission = !props.permission ? true : context.hasPermission(props.permission);
    const isButton = props.onClick || props.type;

    const renderIcon = () => {
        const color = props.color || "inherit";
        return (
            <div className={"icon-button__container " + (props.className || "") + (props.isHidden ? " icon-button__container-hidden" : "")}>
                {renderIconElement(color)}
                {props.rightText}
            </div>);
    };

    const renderIconElement = (color: any) => {
        if (typeof props.icon === "string") {
            return props.icon.split(".").map(icon =>
                <IconMaterial className={"material-icons " + (props.className || "")} key={icon} color={color}
                    data-testid={props.icon}>
                    {icon}
                </IconMaterial>);
        } else {
            return props.icon;
        }
    };

    return <>
        {!hasPermission
            ? <div />
            : props.tooltipTitle
                ? <Tooltip title={t(props.tooltipTitle)} placement={props.tooltipPlacement || "bottom"}>
                    {isButton ? <ButtonIcon {...props} /> : renderIcon()}
                  </Tooltip>
                : isButton ? <ButtonIcon {...props} /> : renderIcon()}
    </>;
};

interface IButtonIconProps {
    align?: "left" | "right";
    disabled?: boolean;
    icon: IconType | JSX.Element;
    isHidden?: boolean;
    isRounded?: boolean;
    onClick?: (event: any) => void;
    type?: "submit" | "reset" | "button";
    className?: string;
    rightText?: string;
    contained?: boolean;
    autoFocus?: boolean;
    color?: "inherit" | "primary" | "secondary";
}

const ButtonIcon = (props: IButtonIconProps) => {
    const [isHighlighted, setIsHighlighted] = useState(false);

    const iconButtonVariant = props.contained ? "contained" : "text";
    const iconColor = props.color || (isHighlighted ? "primary" : "inherit");

    const renderButtonElementByType = () => {
        return props.contained || !props.isRounded ?
            renderRegularButton() : renderActionButton();
    };

    const renderRegularButton = () => {
        return <Button variant={iconButtonVariant}
                       className={`icon-button__button icon-button__button--${iconButtonVariant}`}
                       onClick={props.onClick}
                       color={iconColor}
                       disabled={props.disabled === undefined ? false : props.disabled}
                       onMouseOver={() => setIsHighlighted(true)}
                       onMouseOut={() => setIsHighlighted(false)}
                       onBlur={() => setIsHighlighted(false)}
                       data-testid={typeof props.icon === "string" ? props.icon : ""}
                       type={props.type}
                       autoFocus={props.autoFocus}>
            {renderButtonIconElement()}
            {props.rightText}
        </Button>;
    };

    const renderActionButton = () => {
        return <Fab className={`icon-button__button icon-button__button-rounded`}
                    onClick={props.onClick} color={iconColor}
                    disabled={props.disabled === undefined ? false : props.disabled}
                    onMouseOver={() => setIsHighlighted(true)}
                    onMouseOut={() => setIsHighlighted(false)}
                    onBlur={() => setIsHighlighted(false)}
                    data-testid={typeof props.icon === "string" ? props.icon : ""}
                    type={props.type}
                    autoFocus={props.autoFocus}>
            {renderButtonIconElement()}
            {props.rightText}
        </Fab>;
    };

    const renderButtonIconElement = () => {
        if (typeof props.icon === "string") {
            return props.icon.split(".").map(icon =>
                <i className={"material-icons " + (props.className || "")} key={icon}>{icon}</i>);
        } else {
            return props.icon;
        }
    };

    return <div className={"icon-button__container" +
        ` ${props.isHidden ? "icon-button__container--hidden" : ""}` +
        ` ${props.disabled ? "icon-button__container--disabled" : ""}` +
        ` ${props.align === "right" ? "icon-button__container--right" : ""}` +
        ` ${props.className || ""}`}>
        {renderButtonElementByType()}
    </div>;
};
